import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import type { UploadFileResultDTO } from '@nora-monorepo/file-api-client';
import type {
  FileLifecycleEventData,
  FileResultDTO,
  FolderBrowseChildrenResultDTO,
  FolderBrowseDTO,
  FolderLifecycleEventData,
  SignedUrlUploadRequest,
  TypesenseSearchResponseDTO,
  UpdateFileLocationRequest,
  UserResultDTO,
} from '@nora-monorepo/nora-api-client';
import { isNonEmptyString } from '@nora-monorepo/shared';
import {
  NoraWebViewEventPattern,
  useApiStore,
  useWebViewStore,
} from '@nora-monorepo/ui-shared';

import { currentRouteParam, navigateToRoute } from '#nora-ui/router/navigation';
import { RouteName } from '#nora-ui/router/route-names';

export const useFileStore = defineStore('fileStore', () => {
  const api = useApiStore();
  const webViewStore = useWebViewStore();

  const defaultFolders = ref<FolderBrowseDTO[]>([]);
  const folderLabels = ref<Record<string, string>>({});
  const recentFiles = ref<FileResultDTO[]>([]);
  const selectedFile = ref<FileResultDTO | null>(null);
  const fileOwner = ref<UserResultDTO>();
  const latestFileMutation = ref<FileLifecycleEventData>();
  const latestFolderMutation = ref<FolderLifecycleEventData>();

  function folderPathSegments(fullPath: string): string[] {
    return fullPath.split('/').filter(segment => segment.length > 0);
  }

  function currentFolderParams(): { folderPath: string | string[] } {
    return { folderPath: currentRouteParam('folderPath') ?? [] };
  }

  function rememberFolderLabel(folder: FolderBrowseDTO): void {
    folderLabels.value = {
      ...folderLabels.value,
      [folder.fullPath]: folder.name,
    };
  }

  function getFolderLabel(fullPath: string): string | undefined {
    return folderLabels.value[fullPath];
  }

  function openFolder(folder: FolderBrowseDTO): void {
    rememberFolderLabel(folder);
    navigateToRoute(RouteName.Files, { folderPath: folderPathSegments(folder.fullPath) });
  }

  function openFile(file: FileResultDTO): void {
    setSelectedFile(file);

    if (!isCompleteFile(file)) {
      return;
    }

    navigateToRoute(RouteName.Files, currentFolderParams(), {
      file: file.id,
      fileDomain: file.domain,
      fileDomainId: file.domainId,
      filePath: file.path,
    });
  }

  function closeFileDetails(): void {
    setSelectedFile(null);
    navigateToRoute(RouteName.Files, currentFolderParams());
  }

  async function bootstrap(): Promise<void> {
    await Promise.all([fetchDefaultFolders(), fetchRecentFiles()]);
  }

  async function fetchDefaultFolders(): Promise<FolderBrowseDTO[]> {
    const request = await api.unwrap(api.file.getRootFolderForUser());
    let folders: FolderBrowseDTO[] = [];

    if (request.data) {
      folders = request.data as FolderBrowseDTO[];
    }

    defaultFolders.value = folders;

    return folders;
  }

  async function fetchRecentFiles(): Promise<FileResultDTO[]> {
    const request = await api.unwrap(api.file.getRecentFiles());
    let files: FileResultDTO[] = [];

    if (request.data) {
      files = request.data as FileResultDTO[];
    }

    recentFiles.value = files;

    return files;
  }

  async function fetchFolderContentsByPath(
    path: string,
  ): Promise<FolderBrowseChildrenResultDTO | undefined> {
    const request = await api.unwrap(
      api.file.browse({
        path,
      }),
    );

    return request.data;
  }

  function setSelectedFile(file: FileResultDTO | null): void {
    selectedFile.value = file;
  }

  async function getFileOwner(userId: string): Promise<void> {
    try {
      const response = await api.unwrap(
        api.nexusUser.getUserById({ userId }),
      );
      fileOwner.value = response.data;
    }
    catch (error) {
      console.error('Could not find file owner', error);
    }
  }

  async function uploadFile(
    domain: string,
    domainId: string,
    path: string,
    file: File,
  ): Promise<AxiosResponse<{ data: UploadFileResultDTO }>> {
    const uploadRequest: SignedUrlUploadRequest = {
      domain,
      domainId,
      path,
      name: file.name,
    };

    const uploadUrlResponse = await api.unwrap(
      api.file.getUploadUrl({
        signedUrlUploadRequest: uploadRequest,
      }),
    );

    const uploadUrl = uploadUrlResponse.data?.url;

    if (!isNonEmptyString(uploadUrl)) {
      throw new Error('Could not get upload url');
    }

    const formData = new FormData();
    formData.append('file', file);

    const upload = await axios.post<{ data: UploadFileResultDTO }>(
      uploadUrl,
      formData,
      {},
    );

    return upload;
  }

  async function deleteFile(file: FileResultDTO): Promise<void> {
    if (!isCompleteFile(file)) {
      return;
    }

    await api.file.deleteFile({
      domain: file.domain,
      domainId: file.domainId,
      fileId: file.id,
      path: file.path,
    });

    handleFileLifecycleEvent({
      id: file.id,
      domain: file.domain,
      domainId: file.domainId,
      path: file.path,
      mutationType: 'delete',
    });

    if (selectedFile.value && selectedFile.value.id === file.id) {
      closeFileDetails();
    }
  }

  async function createFolder(
    domain: string,
    domainId: string,
    path: string,
    name: string,
  ): Promise<void> {
    await api.unwrap(
      api.file.createFolder({
        createFolderRequest: {
          domain,
          domainId,
          path,
          name,
        },
      }),
    );
  }

  async function renameFolder(
    domain: string,
    domainId: string,
    path: string,
    newName: string,
  ): Promise<void> {
    await api.unwrap(
      api.file.renameFolder({
        renameFolderRequest: {
          domain,
          domainId,
          path,
          newName,
        },
      }),
    );
  }

  async function deleteFolder(
    domain: string,
    domainId: string,
    path: string,
  ): Promise<void> {
    await api.unwrap(
      api.file.deleteFolder({
        deleteFolderRequest: {
          domain,
          domainId,
          path,
        },
      }),
    );
  }

  async function renameFile(file: FileResultDTO, newName: string): Promise<void> {
    if (!isCompleteFile(file)) {
      return;
    }

    const response = await api.unwrap(
      api.file.renameFile({
        domain: file.domain,
        domainId: file.domainId,
        fileId: file.id,
        path: file.path,
        renameFileRequest: {
          newName,
        },
      }),
    );

    handleFileLifecycleEvent({
      id: file.id,
      domain: file.domain,
      domainId: file.domainId,
      path: file.path,
      mutationType: 'update',
      file: response.data,
    });
  }

  async function updateFileLocation(
    file: FileResultDTO,
    updateFileLocationRequest: UpdateFileLocationRequest,
  ): Promise<void> {
    if (!isCompleteFile(file)) {
      return;
    }

    const response = await api.unwrap(
      api.file.updateFileLocation({
        domain: file.domain,
        domainId: file.domainId,
        fileId: file.id,
        path: file.path,
        updateFileLocationRequest,
      }),
    );

    handleFileLifecycleEvent({
      id: file.id,
      domain: file.domain,
      domainId: file.domainId,
      path: file.path,
      mutationType: 'update',
      file: response.data,
    });
  }

  function handleFileLifecycleEvent(eventData: FileLifecycleEventData): void {
    latestFileMutation.value = eventData;

    if (
      eventData.mutationType === 'delete'
      && selectedFile.value?.id === eventData.id
    ) {
      closeFileDetails();
    }

    if (eventData.mutationType !== 'delete') {
      if (
        eventData.file?.status === 'unscanned'
        && [selectedFile.value, ...recentFiles.value].some(file =>
          file?.id === eventData.id
          && (file.status === 'scanned' || file.status === 'infected'),
        )
      ) {
        return;
      }

      if (eventData.file && selectedFile.value?.id === eventData.id) {
        selectedFile.value = {
          ...eventData.file,
          urls: eventData.file.urls ?? selectedFile.value.urls,
        };
      }

      const recentFileIndex = recentFiles.value.findIndex(
        file => file.id === eventData.id,
      );

      // SSE events never carry signed URLs, so keep the ones already loaded.
      if (eventData.file && recentFileIndex !== -1) {
        recentFiles.value[recentFileIndex] = {
          ...eventData.file,
          urls: eventData.file.urls ?? recentFiles.value[recentFileIndex].urls,
        };
      }

      return;
    }

    recentFiles.value = recentFiles.value.filter(file => file.id !== eventData.id);
  }

  function handleFolderLifecycleEvent(eventData: FolderLifecycleEventData): void {
    latestFolderMutation.value = eventData;
  }

  async function searchFiles(
    domain: string,
    domainId: string,
    path: string,
    query: string,
  ): Promise<TypesenseSearchResponseDTO | null> {
    const response = await api.unwrap(
      api.file.searchFiles({
        domain,
        domainId,
        path,
        query,
      }),
    );

    if (!response.data) {
      return null;
    }

    return response.data as TypesenseSearchResponseDTO;
  }

  function isCompleteFile(
    file?: FileResultDTO | null,
  ): file is Required<
    Pick<FileResultDTO, 'domain' | 'domainId' | 'id' | 'path'>
  >
  & FileResultDTO {
    return (
      file !== undefined
      && file !== null
      && file.domain !== undefined
      && file.domainId !== undefined
      && file.id !== undefined
      && file.path !== undefined
    );
  }

  async function loadFileDetails(options: {
    domain: string;
    domainId: string;
    id: string;
    path: string;
  }): Promise<FileResultDTO | undefined> {
    const file = await api.unwrap(
      api.file.getFileMetadata({
        domain: options.domain,
        domainId: options.domainId,
        fileId: options.id,
        path: options.path,
      }),
    );

    if (!file.data) {
      throw new Error('File not found');
    }

    return file.data;
  }

  async function openFileDetails(options: {
    domain: string;
    domainId: string;
    id: string;
    path: string;
  }): Promise<void> {
    if (webViewStore.isWebView) {
      webViewStore.sendEventToNativeLayer(
        NoraWebViewEventPattern.OPEN_FILE,
        options,
      );

      return;
    }

    const file = await loadFileDetails(options);

    if (!file) {
      closeFileDetails();

      return;
    }

    openFile(file);
  }

  return {
    fileOwner,
    selectedFile,
    defaultFolders,
    recentFiles,
    latestFileMutation,
    latestFolderMutation,

    bootstrap,
    closeFileDetails,
    getFileOwner,
    fetchFolderContentsByPath,
    fetchRecentFiles,
    fetchDefaultFolders,
    getFolderLabel,
    openFile,
    openFolder,
    setSelectedFile,
    uploadFile,
    createFolder,
    renameFolder,
    deleteFolder,
    deleteFile,
    renameFile,
    updateFileLocation,
    handleFileLifecycleEvent,
    handleFolderLifecycleEvent,
    searchFiles,
    openFileDetails,
    loadFileDetails,
  };
});
